import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { CurrentUser } from '../../../common/models/user';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { AllocationService } from '../../../common/services/allocation.service';
import { AllocationFormType, AllocationSummary, GetAllocationsFilter } from '../../../common/models/allocation';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { Subject, takeUntil } from 'rxjs';
import { Guid } from 'guid-typescript';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';
import { AllocationsFilterBuilder } from '../../../common/components/filter/builders/allocations-filter-builder';
import { AllocationStatus } from '../../../common/enums/allocation-status';

@Component({
    selector: 'app-allocations',
    templateUrl: './allocations.component.html',
    styleUrl: './allocations.component.scss',
    imports: [MatCardModule, MatMenuModule, MatButtonModule, MatPaginatorModule, MatTooltipModule, MatIconModule, MatDialogModule, MatSnackBarModule, MatCheckboxModule, CommonModule],
    standalone: true
})
export class AllocationsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    allocations: AllocationSummary[] = [];
    filteredAllocations: AllocationSummary[] = [];
    public AllocationStatus = AllocationStatus;
    public AllocationFormType = AllocationFormType;
    user: CurrentUser | null = null;
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };

    constructor(
      private router: Router,
      private filterBuilder: AllocationsFilterBuilder,
      private allocationService: AllocationService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });   
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchAllocations();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters();
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });    
      
    }

    fetchAllocations(): void {
          const savedFilterValues = this.filterService.getFiltersInScope();
        
        const associationId = this.user?.associationId ?? '';
        const municipalityId = this.user?.municipalityId ?? '';

        const filter = new GetAllocationsFilter(
          municipalityId, 
          associationId, 
          savedFilterValues?.status, 
          savedFilterValues?.year
        );

        this.allocationService.getAllocations(filter).pipe(takeUntil(this.destroy$)).subscribe({
          next: (data) => {
            this.allocations = data.map(a => AllocationSummary.fromJson(a));
            this.filteredAllocations = [...this.allocations];
          },
          error: (error) => {
            console.error('Greška prilikom dohvata izravnih dodjela', error);
          }
        });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredAllocations = this.allocations.filter((allocation: AllocationSummary) => {
          return (
            allocation.activityName.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredAllocations = [...this.allocations];
      }
    }

    confirmSubmit(allocation: AllocationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite predaju',
          message: `Jeste li sigurni da želite predati izravnu dodjelu "${allocation.activityName || ''}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.submitAllocation(allocation);
        }
      });
    }

    private submitAllocation(allocation: AllocationSummary): void {
      this.allocationService.changeStatus(allocation.id, AllocationStatus.Submitted).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchAllocations();
          this.snackBar.open('Izravna dodjela je poslana na odobrenje', 'Zatvori', this.snackbarConfig);
        },
        error: (error) => {
          console.error('Greška prilikom slanja izravne dodjele na odobrenje', error);
        }
      });
    }

    confirmResubmit(allocation: AllocationSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite ponovnu predaju',
          message: `Jeste li sigurni da želite ponovno predati izravnu dodjelu "${allocation.activityName || ''}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.resubmitAllocation(allocation);
        }
      });
    }

    private resubmitAllocation(allocation: AllocationSummary): void {
      this.allocationService.changeStatus(allocation.id, AllocationStatus.Resubmitted).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchAllocations();
          this.snackBar.open('Izravna dodjela je poslana na odobrenje', 'Zatvori', this.snackbarConfig); 
        },
        error: (error) => {
          console.error('Greška prilikom slanja izravne dodjele na odobrenje', error);
        }
      });
    }

    confirmDelete(allocation: AllocationSummary): void {
            const dialogRef = this.dialog.open(GenericDialogComponent, {
              width: '50%',
              data: {
                title: 'Potvrdite brisanje',
                message: `Jeste li sigurni da želite obrisati izravnu dodjelu sredstava?`,
                confirmButtonText: 'Da',
                cancelButtonText: 'Ne',
              },
            });
        
            dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
              if (result === true) {
                this.deleteAllocation(allocation.id);
              }
            });
    }

    deleteAllocation(allocationId: Guid): void {
        this.allocationService.deleteAllocation(allocationId).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.fetchAllocations();
            this.snackBar.open('Izravna dodjela sredstava je obrisana', 'Zatvori', this.snackbarConfig);
          },
          error: (error) => {
            console.error('Error deleting grant', error);
          }
        });
      }

      downloadForms(allocation: AllocationSummary, formTypes: AllocationFormType[]): void {
          this.allocationService.downloadForms(allocation.id, formTypes).pipe(takeUntil(this.destroy$)).subscribe({
              next: (blob) => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'dokumentacija-izravna-dodjela-sredstava.zip';
                  a.click();
                  window.URL.revokeObjectURL(url);
              },
              error: (err) => {
                  console.error('Error downloading allocation forms', err);
              }
          });
      }

      goToCreateAllocation(): void {
        this.router.navigate(['association', 'allocations', 'create']);
      }

      goToEditAllocation(allocation: AllocationSummary): void {
        this.router.navigate(['association', 'allocations', allocation.id.toString(), 'edit']);
      }

      goToViewAllocation(allocation: AllocationSummary): void {
        this.router.navigate(['association', 'allocations', allocation.id.toString(), 'view']);
      }
}
